import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Share07 } from '@untitledui/icons';
import { toast } from 'react-toastify';
import { workoutService } from '../services/workoutService';
import { memberService, MemberResponse } from '../services/memberService';
import { exerciseService } from '../services/exerciseService';
import { workoutShareService } from '../services/workoutShareService';
import { 
  Workout, 
  WorkoutRequest, 
  Exercise, 
  MemberExercise, 
  MemberExerciseRequest, 
  WorkoutSet,
  WorkoutSetEditing,
  ExerciseFilters 
} from '../types/Workout';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from './untitled-ui/base/buttons/button';

// Sortable Exercise Card Component
interface SortableExerciseCardProps {
  memberExercise: MemberExercise;
  editingExercise: { [key: number]: MemberExercise };
  savingIds: Set<number>;
  startEditingExercise: (memberExercise: MemberExercise) => void;
  cancelEditingExercise: (memberExerciseId: number) => void;
  handleUpdateExerciseSets: (memberExercise: MemberExercise) => void;
  handleRemoveExercise: (memberExercise: MemberExercise) => void;
  handleSetChange: (memberExerciseId: number, setIndex: number, field: keyof WorkoutSet, value: any) => void;
  handleAddSet: (memberExerciseId: number) => void;
  handleRemoveSet: (memberExerciseId: number, setIndex: number) => void;
}

const SortableExerciseCard: React.FC<SortableExerciseCardProps> = ({
  memberExercise,
  editingExercise,
  savingIds,
  startEditingExercise,
  cancelEditingExercise,
  handleUpdateExerciseSets,
  handleRemoveExercise,
  handleSetChange,
  handleAddSet,
  handleRemoveSet,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: memberExercise.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Fix: Use the editingExercise state properly
  const isThisExerciseEditing = editingExercise[memberExercise.id];
  const currentExercise = isThisExerciseEditing || memberExercise;
  const isThisExerciseSaving = savingIds.has(memberExercise.id);

  return (
    <div ref={setNodeRef} style={style} className="exercise-card">
      <div className="exercise-header">
        <div className="exercise-title">
          <div 
            className="drag-handle"
            {...attributes}
            {...listeners}
            title="Drag to reorder"
          >
            ⋮⋮
          </div>
          {memberExercise.exerciseImageUrl && (
            <img 
              src={memberExercise.exerciseImageUrl} 
              alt={memberExercise.exerciseName}
              className="exercise-image-small"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <div>
            <h3>{memberExercise.exerciseName}</h3>
            {memberExercise.exerciseMuscleGroups && (
              <span className="muscle-groups">{memberExercise.exerciseMuscleGroups}</span>
            )}
          </div>
        </div>
      </div>

      {memberExercise.exerciseDescription && (
        <p className="exercise-description">{memberExercise.exerciseDescription}</p>
      )}

      <div className="sets-container">
        <div className="sets-header">
          <span>Set</span>
          <span>Weight (kg)</span>
          <span>Reps</span>
          <span>Warmup</span>
          {isThisExerciseEditing && <span>Actions</span>}
        </div>

        {currentExercise.sets.map((set, setIndex) => (
          <div key={setIndex} className="set-row">
            <span className="set-number">{set.setNumber}</span>
            {isThisExerciseEditing ? (
              <>
                <input
                  type="number"
                  className="form-control set-input"
                  value={set.weight}
                  onChange={(e) => handleSetChange(memberExercise.id, setIndex, 'weight', e.target.value)}
                  min="0"
                  step="0.5"
                />
                <input
                  type="number"
                  className="form-control set-input"
                  value={set.reps}
                  onChange={(e) => handleSetChange(memberExercise.id, setIndex, 'reps', e.target.value)}
                  min="0"
                />
                <input
                  type="checkbox"
                  className="warmup-checkbox"
                  checked={set.isWarmup}
                  onChange={(e) => handleSetChange(memberExercise.id, setIndex, 'isWarmup', e.target.checked)}
                />
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => handleRemoveSet(memberExercise.id, setIndex)}
                >
                  Remove
                </button>
              </>
            ) : (
              <>
                <span>{set.weight}kg</span>
                <span>{set.reps}</span>
                <span>{set.isWarmup ? '✓' : ''}</span>
                <span></span>
              </>
            )}
          </div>
        ))}

        {isThisExerciseEditing && (
          <button
            className="btn btn-outline-primary add-set-btn"
            onClick={() => handleAddSet(memberExercise.id)}
          >
            + Add Set
          </button>
        )}
      </div>

      <div className="exercise-actions-bottom">
        {isThisExerciseEditing ? (
          <>
            <button
              className="exercise-action-btn save-btn"
              onClick={() => handleUpdateExerciseSets(memberExercise)}
              disabled={isThisExerciseSaving}
            >
              {isThisExerciseSaving ? '⏳' : '💾'}
              {isThisExerciseSaving && <span className="saving-text">Saving...</span>}
            </button>
            <button
              className="exercise-action-btn cancel-btn"
              onClick={() => cancelEditingExercise(memberExercise.id)}
              disabled={isThisExerciseSaving}
            >
              ✖️
            </button>
          </>
        ) : (
          <>
            <button
              className="exercise-action-btn edit-btn"
              onClick={() => startEditingExercise(memberExercise)}
            >
              ✏️
            </button>
            <button
              className="exercise-action-btn delete-btn"
              onClick={() => handleRemoveExercise(memberExercise)}
            >
              🗑️
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const WorkoutPlan: React.FC = () => {
  const { memberId, workoutId } = useParams<{ memberId: string; workoutId?: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberResponse | null>(null);
  const [memberError, setMemberError] = useState(false);
  useEffect(() => {
    let active = true;
    setMember(null);
    setMemberError(false);
    if (!memberId) return;
    memberService.getMemberById(Number(memberId)).then(response => {
      if (!active) return;
      if (response.success && response.data) setMember(response.data);
      else setMemberError(true);
    }).catch(() => { if (active) setMemberError(true); });
    return () => { active = false; };
  }, [memberId]);
  const memberIdentity = member ? <><strong>{member.fullName}</strong> · Member {member.memberNumber}</> : memberError ? `Member record ${memberId} · Unable to load member details` : 'Loading member details…';
  
  // State for workout list view
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreatingWorkout, setIsCreatingWorkout] = useState(false);
  const [newWorkoutName, setNewWorkoutName] = useState('');
  const [editingWorkoutName, setEditingWorkoutName] = useState<{ [key: number]: string }>({});
  const [savingWorkoutName, setSavingWorkoutName] = useState<Set<number>>(new Set());
  const [generatingShareToken, setGeneratingShareToken] = useState(false);
  
  // State for individual workout editing view
  const [currentWorkout, setCurrentWorkout] = useState<Workout | null>(null);
  const [workoutExercises, setWorkoutExercises] = useState<MemberExercise[]>([]);
  const [editingExercise, setEditingExercise] = useState<{ [key: number]: MemberExercise }>({});
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  
  // State for exercise selection
  const [isAddingExercise, setIsAddingExercise] = useState(false);
  const [availableExercises, setAvailableExercises] = useState<Exercise[]>([]);
  const [filteredExercises, setFilteredExercises] = useState<Exercise[]>([]);
  const [exerciseSearchTerm, setExerciseSearchTerm] = useState('');
  const [exerciseFilters, setExerciseFilters] = useState<ExerciseFilters>({});
  const [muscleGroups, setMuscleGroups] = useState<string[]>([]);
  const [equipmentList, setEquipmentList] = useState<string[]>([]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (memberId) {
      if (workoutId) {
        // Load specific workout for editing
        loadWorkoutForEditing(parseInt(workoutId));
      } else {
        // Load all workouts for the member
        loadMemberWorkouts();
      }
    }
  }, [memberId, workoutId]);

  useEffect(() => {
    loadAvailableExercises();
  }, []);

  useEffect(() => {
    filterExercises();
  }, [exerciseSearchTerm, exerciseFilters, availableExercises]);

  const loadMemberWorkouts = async () => {
    try {
      setLoading(true);
      const response = await workoutService.getMemberWorkouts(parseInt(memberId!));
      
      if (response.success && response.data) {
        // Sort workouts by creation date ascending (first created first, newest last)
        const sortedWorkouts = response.data.sort((a: Workout, b: Workout) => 
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
        setWorkouts(sortedWorkouts);
      } else {
        setWorkouts([]);
      }
    } catch (error) {
      console.error('Error fetching workouts:', error);
      toast.error('Error fetching workouts');
      setWorkouts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkoutForEditing = async (workoutIdNum: number) => {
    try {
      setLoading(true);
      const response = await workoutService.getWorkout(workoutIdNum);
      
      if (response.success && response.data) {
        setCurrentWorkout(response.data);
        setWorkoutExercises(response.data.exercises || []);
      } else {
        toast.error('Workout not found');
        navigate(`/workout-plan/${memberId}`);
      }
    } catch (error) {
      console.error('Error fetching workout:', error);
      toast.error('Error fetching workout');
      navigate(`/workout-plan/${memberId}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableExercises = async () => {
    try {
      const response = await exerciseService.getAllExercises();
      
      if (response.success && response.data) {
        setAvailableExercises(response.data);
        setMuscleGroups(exerciseService.getMuscleGroupsFromExercises(response.data));
        setEquipmentList(exerciseService.getEquipmentFromExercises(response.data));
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
      toast.error('Error fetching exercise library');
    }
  };

  const filterExercises = () => {
    let filtered = [...availableExercises];
    
    // Filter by search term
    if (exerciseSearchTerm.trim()) {
      const searchLower = exerciseSearchTerm.toLowerCase();
      filtered = filtered.filter(exercise => 
        exercise.name.toLowerCase().includes(searchLower) ||
        (exercise.description && exercise.description.toLowerCase().includes(searchLower)) ||
        (exercise.muscleGroups && exercise.muscleGroups.toLowerCase().includes(searchLower))
      );
    }
    
    // Filter by muscle group
    if (exerciseFilters.muscleGroup) {
      filtered = filtered.filter(exercise => 
        exercise.muscleGroups && exercise.muscleGroups.includes(exerciseFilters.muscleGroup!)
      );
    }
    
    // Filter by equipment
    if (exerciseFilters.equipment) {
      filtered = filtered.filter(exercise => 
        exercise.equipment && exercise.equipment.toLowerCase().includes(exerciseFilters.equipment!.toLowerCase())
      );
    }
    
    setFilteredExercises(filtered);
  };

  const handleCreateWorkout = async () => {
    if (!newWorkoutName.trim()) {
      toast.error('Please enter a workout name');
      return;
    }

    try {
      setIsCreatingWorkout(true);
      const request: WorkoutRequest = {
        memberId: parseInt(memberId!),
        workoutName: newWorkoutName.trim()
      };

      const response = await workoutService.createWorkout(request);
      
      if (response.success && response.data) {
        toast.success('Workout created successfully');
        setNewWorkoutName('');
        loadMemberWorkouts();
    } else {
        toast.error('Error creating workout');
      }
    } catch (error) {
      console.error('Error creating workout:', error);
      toast.error('Error creating workout');
    } finally {
      setIsCreatingWorkout(false);
    }
  };

  const handleShareWorkouts = async () => {
    if (!memberId) return;

    try {
      setGeneratingShareToken(true);
      const response = await workoutShareService.generateShareToken(parseInt(memberId));
      
      if (response.success && response.data) {
        const shareUrl = `${window.location.origin}/workout-share/${response.data.token}`;
        
        if (navigator.share) {
          // Use native share API if available (mobile devices)
          await navigator.share({
            title: 'Workout Plan',
            text: 'Check out this workout plan!',
            url: shareUrl,
      });
    } else {
          // Fallback to copying to clipboard
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Share link copied to clipboard!');
        }
      } else {
        toast.error('Error generating share link');
      }
    } catch (error) {
      console.error('Error sharing workout:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        // User cancelled the share, don't show error
        return;
      }
      toast.error('Error generating share link');
    } finally {
      setGeneratingShareToken(false);
    }
  };

  const handleDeleteWorkout = async (workout: Workout) => {
    if (!window.confirm(`Are you sure you want to delete "${workout.workoutName}"?`)) {
      return;
    }

    try {
      const response = await workoutService.deleteWorkout(workout.id, workout.memberId);
      
      if (response.success) {
        toast.success('Workout deleted successfully');
        loadMemberWorkouts();
    } else {
        toast.error('Error deleting workout');
      }
    } catch (error) {
      console.error('Error deleting workout:', error);
      toast.error('Error deleting workout');
    }
  };

  const startEditingWorkoutName = (workout: Workout) => {
    setEditingWorkoutName({
      ...editingWorkoutName,
      [workout.id]: workout.workoutName
    });
  };

  const cancelEditingWorkoutName = (workoutId: number) => {
    const newEditingState = { ...editingWorkoutName };
    delete newEditingState[workoutId];
    setEditingWorkoutName(newEditingState);
  };

  const handleWorkoutNameChange = (workoutId: number, newName: string) => {
    setEditingWorkoutName({
      ...editingWorkoutName,
      [workoutId]: newName
    });
  };

  const handleSaveWorkoutName = async (workout: Workout) => {
    const newName = editingWorkoutName[workout.id];
    if (!newName || !newName.trim()) {
      toast.error('Please enter a workout name');
      return;
    }

    if (newName.trim() === workout.workoutName) {
      cancelEditingWorkoutName(workout.id);
      return;
    }

    try {
      setSavingWorkoutName(prev => new Set(prev).add(workout.id));

      const request: WorkoutRequest = {
        memberId: workout.memberId,
        workoutName: newName.trim()
      };

      const response = await workoutService.updateWorkout(workout.id, request);
      
        if (response.success) {
        toast.success('Workout name updated successfully');
        cancelEditingWorkoutName(workout.id);
        loadMemberWorkouts();
      } else {
        toast.error('Error updating workout name');
      }
    } catch (error) {
      console.error('Error updating workout name:', error);
      toast.error('Error updating workout name');
    } finally {
      setSavingWorkoutName(prev => {
        const newSet = new Set(prev);
        newSet.delete(workout.id);
        return newSet;
      });
    }
  };

  const handleAddExerciseToWorkout = async (exercise: Exercise) => {
    if (!currentWorkout) return;

    // Check if exercise already exists in workout
    const existingExercise = workoutExercises.find(we => we.exerciseId === exercise.id);
    if (existingExercise) {
      toast.error('Exercise already exists in this workout');
      return;
    }

    try {
      const request: MemberExerciseRequest = {
        workoutId: currentWorkout.id,
        exerciseId: exercise.id,
        sets: [{
          setNumber: 1,
          weight: 0,
          reps: 0,
          isWarmup: false
        }]
      };

      const response = await exerciseService.addExerciseToWorkout(request);
      
      if (response.success && response.data) {
        setWorkoutExercises([...workoutExercises, response.data]);
        setIsAddingExercise(false);
        setExerciseSearchTerm('');
        toast.success('Exercise added to workout');
      } else {
        toast.error('Error adding exercise to workout');
      }
    } catch (error) {
      console.error('Error adding exercise:', error);
      toast.error('Error adding exercise to workout');
    }
  };

  const handleUpdateExerciseSets = async (memberExercise: MemberExercise) => {
    try {
      setSavingIds(prev => new Set(prev).add(memberExercise.id));
      
      // Get the edited exercise data with the user's changes
      const editedExercise = editingExercise[memberExercise.id];
      if (!editedExercise) {
        toast.error('No changes to save');
        return;
      }
      
      // Process sets to ensure proper number values for API
      const processedSets = editedExercise.sets.map(set => ({
        ...set,
        weight: (set.weight as any) === '' ? 0 : Number(set.weight),
        reps: (set.reps as any) === '' ? 0 : Number(set.reps)
      }));
      
      const request: MemberExerciseRequest = {
        workoutId: memberExercise.workoutId,
        exerciseId: memberExercise.exerciseId,
        sets: processedSets
      };

      const response = await exerciseService.updateExerciseInWorkout(memberExercise.id, request);
      
      if (response.success && response.data) {
        // Update the exercise in the list
        setWorkoutExercises(prev => 
          prev.map(ex => ex.id === memberExercise.id ? response.data! : ex)
        );
        
        // Clear editing state
        const newEditingState = { ...editingExercise };
        delete newEditingState[memberExercise.id];
        setEditingExercise(newEditingState);
        
        toast.success('Exercise updated successfully');
      } else {
        toast.error('Error updating exercise');
      }
    } catch (error) {
      console.error('Error updating exercise:', error);
      toast.error('Error updating exercise');
    } finally {
      setSavingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(memberExercise.id);
        return newSet;
      });
    }
  };

  const handleRemoveExercise = async (memberExercise: MemberExercise) => {
    if (!window.confirm(`Remove "${memberExercise.exerciseName}" from workout?`)) {
      return;
    }

    try {
      const response = await exerciseService.removeMemberExercise(memberExercise.id);
      
      if (response.success) {
        setWorkoutExercises(prev => prev.filter(ex => ex.id !== memberExercise.id));
        toast.success('Exercise removed from workout');
      } else {
        toast.error('Error removing exercise');
      }
    } catch (error) {
      console.error('Error removing exercise:', error);
      toast.error('Error removing exercise');
    }
  };

  const startEditingExercise = (memberExercise: MemberExercise) => {
    setEditingExercise({
      ...editingExercise,
      [memberExercise.id]: { ...memberExercise }
    });
  };

  const cancelEditingExercise = (memberExerciseId: number) => {
    const newEditingState = { ...editingExercise };
    delete newEditingState[memberExerciseId];
    setEditingExercise(newEditingState);
  };

  const handleSetChange = (memberExerciseId: number, setIndex: number, field: keyof WorkoutSet, value: any) => {
    const currentExercise = editingExercise[memberExerciseId];
    if (!currentExercise) return;

    const updatedSets = [...currentExercise.sets];
    
    // Handle different field types
    let processedValue = value;
    if (field === 'isWarmup') {
      processedValue = value;
    } else if (field === 'weight' || field === 'reps') {
      // Allow empty string for editing, will be converted to number when saving
      processedValue = value === '' ? '' : Number(value);
    }
    
    updatedSets[setIndex] = {
      ...updatedSets[setIndex],
      [field]: processedValue as any // Allow string values during editing
    };

    setEditingExercise({
      ...editingExercise,
      [memberExerciseId]: {
        ...currentExercise,
        sets: updatedSets
      }
    });
  };

  const handleAddSet = (memberExerciseId: number) => {
    const currentExercise = editingExercise[memberExerciseId];
    if (!currentExercise) return;

    const newSet: WorkoutSet = {
      setNumber: currentExercise.sets.length + 1,
      weight: 0,
      reps: 0,
      isWarmup: false
    };

    setEditingExercise({
      ...editingExercise,
      [memberExerciseId]: {
        ...currentExercise,
        sets: [...currentExercise.sets, newSet]
      }
    });
  };

  const handleRemoveSet = (memberExerciseId: number, setIndex: number) => {
    const currentExercise = editingExercise[memberExerciseId];
    if (!currentExercise) return;

    const updatedSets = currentExercise.sets.filter((_, index) => index !== setIndex);
    // Renumber the sets
    const renumberedSets = updatedSets.map((set, index) => ({
      ...set,
      setNumber: index + 1
    }));

    setEditingExercise({
      ...editingExercise,
      [memberExerciseId]: {
        ...currentExercise,
        sets: renumberedSets
      }
    });
  };

  // Handle drag end for exercise reordering
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = workoutExercises.findIndex(ex => ex.id === active.id);
    const newIndex = workoutExercises.findIndex(ex => ex.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistic update
    const reorderedExercises = arrayMove(workoutExercises, oldIndex, newIndex);
    setWorkoutExercises(reorderedExercises);

    try {
      // Call API to persist the new order
      const memberExerciseIds = reorderedExercises.map(ex => ex.id);
      const response = await workoutService.reorderExercises(currentWorkout!.id, memberExerciseIds);
      
      if (!response.success) {
        // Revert on error
        setWorkoutExercises(workoutExercises);
        toast.error('Failed to reorder exercises');
      } else {
        toast.success('Exercise order updated');
      }
    } catch (error) {
      // Revert on error
      setWorkoutExercises(workoutExercises);
      console.error('Error reordering exercises:', error);
      toast.error('Failed to reorder exercises');
    }
  };

  // WORKOUT LIST VIEW
  if (!workoutId) {
    return (
      <div className="u-page workout-plan-container">
        <div className="u-page-header workout-page-header">
          <div><button type="button" className="u-back-link" onClick={() => navigate('/members')}><ArrowLeft />Members</button><h2>Workout plans</h2><p>{memberIdentity}</p></div>
          <div className="header-actions">
            <Button size="md" color="secondary" iconLeading={Share07} onPress={handleShareWorkouts} isLoading={generatingShareToken}>Share</Button>
            <Button size="md" color="primary" iconLeading={Plus} onPress={() => setIsCreatingWorkout(!isCreatingWorkout)}>Create workout</Button>
          </div>
        </div>

        {isCreatingWorkout && (
          <div className="create-workout-form">
            <div className="form-group">
              <input
                type="text"
                className="form-control"
                placeholder="Enter workout name"
                value={newWorkoutName}
                onChange={(e) => setNewWorkoutName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateWorkout();
                  }
                }}
              />
              <div className="form-actions">
                <button 
                  className="btn btn-primary"
                  onClick={handleCreateWorkout}
                  disabled={!newWorkoutName.trim()}
                >
                  Create
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setIsCreatingWorkout(false);
                    setNewWorkoutName('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading-state">Loading workouts...</div>
        ) : (
          <div className="workouts-grid">
            {workouts.length === 0 ? (
              <div className="empty-state">
                <p>No workouts created yet.</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => setIsCreatingWorkout(true)}
                >
                  Create Your First Workout
                </button>
              </div>
            ) : (
              workouts.map(workout => {
                const isEditingName = editingWorkoutName[workout.id] !== undefined;
                const isSavingName = savingWorkoutName.has(workout.id);

                return (
                                    <div key={workout.id} className="workout-card">
                    <div className="workout-card-header">
                      <div className="workout-title-section">
                        {!isEditingName ? (
                          <div className="workout-title-display">
                            <h3 
                              className="workout-name-clickable"
                              onClick={() => startEditingWorkoutName(workout)}
                              title="Click to rename"
                            >
                              {workout.workoutName}
                            </h3>
                          </div>
                        ) : (
                          <div className="workout-title-edit">
                            <input
                              type="text"
                              className="form-control workout-name-input"
                              value={editingWorkoutName[workout.id]}
                              onChange={(e) => handleWorkoutNameChange(workout.id, e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveWorkoutName(workout);
                                } else if (e.key === 'Escape') {
                                  cancelEditingWorkoutName(workout.id);
                                }
                              }}
                              disabled={isSavingName}
                              autoFocus
                            />
                            <div className="workout-name-actions">
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleSaveWorkoutName(workout)}
                                disabled={isSavingName}
                              >
                                {isSavingName ? '...' : '✓'}
                              </button>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => cancelEditingWorkoutName(workout.id)}
                                disabled={isSavingName}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                                      <div className="workout-card-body">
                      <p className="exercise-count">
                        {workout.exercises?.length || 0} exercise(s)
                      </p>
                      
                      {workout.exercises && workout.exercises.length > 0 && (
                        <div className="exercises-preview">
                          {workout.exercises.slice(0, 3).map((exercise, index) => (
                            <div key={exercise.id} className="exercise-preview-item">
                              <div className="exercise-info">
                                {exercise.exerciseImageUrl ? (
                                  <img
                                    src={exercise.exerciseImageUrl}
                                    alt={exercise.exerciseName}
                                    className="exercise-preview-image"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="exercise-preview-placeholder">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                      <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z" fill="#95a5a6"/>
                                    </svg>
                                  </div>
                                )}
                                <span className="exercise-name">{exercise.exerciseName}</span>
                              </div>
                              <span className="exercise-sets-count">
                                {exercise.sets?.length || 0} sets
                              </span>
                            </div>
                          ))}
                          {workout.exercises.length > 3 && (
                            <div className="exercise-preview-item more-exercises">
                              <div className="exercise-info">
                                <div className="exercise-preview-placeholder">
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" fill="#95a5a6"/>
                                  </svg>
                                </div>
                                <span className="exercise-name">
                                  +{workout.exercises.length - 3} more...
                                </span>
                              </div>
                        </div>
                          )}
                        </div>
                      )}
                      
                                            <p className="workout-date">
                        Created: {new Date(workout.createdAt).toLocaleDateString()}
                      </p>
                      </div>

                    {/* Modern Action Buttons */}
                    <div className="workout-actions-bottom">
                      <button 
                        className="workout-action-btn edit-btn"
                        onClick={() => navigate(`/workout-plan/${memberId}/${workout.id}`)}
                        title="Edit workout"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button 
                        className="workout-action-btn delete-btn"
                        onClick={() => handleDeleteWorkout(workout)}
                        title="Delete workout"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="m3 6 3 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="m10 11 0 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="m14 11 0 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
              );
              })
            )}
          </div>
        )}
      </div>
    );
  }

  // INDIVIDUAL WORKOUT EDITING VIEW
  if (loading) {
    return <div className="loading-state">Loading workout...</div>;
  }

  if (!currentWorkout) {
    return <div className="error-state">Workout not found</div>;
  }

  return (
    <div className="u-page workout-plan-container">
      <div className="u-page-header workout-page-header">
        <div><button type="button" className="u-back-link" onClick={() => navigate(`/workout-plan/${memberId}`)}><ArrowLeft />Workout plans</button><h2>{currentWorkout.workoutName}</h2><p>{memberIdentity}</p></div>
        <Button size="md" color="primary" iconLeading={Plus} onPress={() => setIsAddingExercise(!isAddingExercise)}>Add exercise</Button>
      </div>

      {/* Exercise Selection Modal */}
      {isAddingExercise && (
        <div className="exercise-selection-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Exercise</h3>
              <button 
                className="close-btn"
                onClick={() => setIsAddingExercise(false)}
              >
                ×
              </button>
            </div>
            
            <div className="exercise-filters">
                  <input
                    type="text"
                className="form-control"
                placeholder="Search exercises..."
                value={exerciseSearchTerm}
                onChange={(e) => setExerciseSearchTerm(e.target.value)}
              />
              
              <select
                className="form-control"
                value={exerciseFilters.muscleGroup || ''}
                onChange={(e) => setExerciseFilters({
                  ...exerciseFilters,
                  muscleGroup: e.target.value || undefined
                })}
              >
                <option value="">All Muscle Groups</option>
                {muscleGroups.map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
              
              <select
                className="form-control"
                value={exerciseFilters.equipment || ''}
                onChange={(e) => setExerciseFilters({
                  ...exerciseFilters,
                  equipment: e.target.value || undefined
                })}
              >
                <option value="">All Equipment</option>
                {equipmentList.map(equipment => (
                  <option key={equipment} value={equipment}>{equipment}</option>
                ))}
              </select>
            </div>

            <div className="exercise-list">
              {filteredExercises.map(exercise => (
                <div key={exercise.id} className="exercise-item">
                  {exercise.imageUrl && (
                    <img 
                      src={exercise.imageUrl} 
                      alt={exercise.name}
                      className="exercise-image"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="exercise-info">
                    <h4>{exercise.name}</h4>
                    {exercise.description && <p>{exercise.description}</p>}
                    {exercise.muscleGroups && (
                      <span className="muscle-groups">{exercise.muscleGroups}</span>
                    )}
                    {exercise.equipment && (
                      <span className="equipment">Equipment: {exercise.equipment}</span>
                    )}
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleAddExerciseToWorkout(exercise)}
                  >
                    Add
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Workout Exercises */}
      <div className="workout-exercises">
        {workoutExercises.length === 0 ? (
          <div className="empty-state">
            <p>No exercises in this workout yet.</p>
            <button 
              className="btn btn-primary"
              onClick={() => setIsAddingExercise(true)}
            >
              Add Your First Exercise
            </button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={workoutExercises.map(ex => ex.id)}
              strategy={verticalListSortingStrategy}
            >
              {workoutExercises.map(memberExercise => {
                return (
                  <SortableExerciseCard
                    key={memberExercise.id}
                    memberExercise={memberExercise}
                    editingExercise={editingExercise}
                    savingIds={savingIds}
                    startEditingExercise={startEditingExercise}
                    cancelEditingExercise={cancelEditingExercise}
                    handleUpdateExerciseSets={handleUpdateExerciseSets}
                    handleRemoveExercise={handleRemoveExercise}
                    handleSetChange={handleSetChange}
                    handleAddSet={handleAddSet}
                    handleRemoveSet={handleRemoveSet}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
          )}
      </div>
    </div>
  );
};

export default WorkoutPlan; 
